/**
 * Format an ISO timestamp as a compact relative-time label
 * suitable for inline use (notification timestamps, activity feeds, etc.).
 *
 * <p>Returns labels like {@code "just now"}, {@code "5m ago"},
 * {@code "3h ago"}, {@code "2d ago"}, or a {@code YYYY-MM-DD} date string
 * for anything older than 7 days. Date formatting is intentionally
 * Intl-free per the frontend convention.</p>
 */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';

  const diffSec = Math.max(0, Math.floor((now - ts) / 1000));
  if (diffSec < 30) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;

  const d = new Date(ts);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
